import React, { useState } from "react";
import useCopyDataset from "hooks/useCopyDataset";
import { renderToStaticMarkup } from "react-dom/server";
import { Duration, Instant } from "@js-joda/core";
import { formatDatasetValue } from "utils/value-format-utils";
import { InstantDoubleValue } from "../../../api/model/dataset/InstantDoubleValue";
import { DatasetDataInfo } from "../../../models/dataset-models";
import DatasetTooltipText, { MappedValue } from "../../basic/dataset-tooltip-text/DatasetTooltipText";
import "./DatasetButtonComponent.scss";

const DOUBLE_CLICK_THRESHOLD = 300;

type DatasetButtonProps = {
  id: string;
  value: string | undefined;
  selected: boolean;
  dataset: DatasetDataInfo;
  data: InstantDoubleValue | null | undefined;
  time: Instant;
  style?: React.CSSProperties;
  compactMode?: boolean;
  setIdentifiers(id: string, ctrlPressed: boolean, doubleClick: boolean): void;
};

const DatasetButtonComponent = (props: DatasetButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [clickTime, setClickTime] = useState(Instant.now());
  useCopyDataset(isHovered, props.dataset.title, props.dataset.identifier);

  const onButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const now = Instant.now();

    if (clickTime) {
      const diff = Duration.between(clickTime, now).toMillis();

      if (e.ctrlKey && diff < DOUBLE_CLICK_THRESHOLD) {
        props.setIdentifiers(props.dataset.identifier as string, true, true);
      } else if (e.ctrlKey && diff >= DOUBLE_CLICK_THRESHOLD) {
        props.setIdentifiers(props.dataset.identifier as string, true, false);
      } else if (!e.ctrlKey && diff < DOUBLE_CLICK_THRESHOLD) {
        props.setIdentifiers(props.dataset.identifier as string, false, true);
      }
    }

    setClickTime(now);
  };

  const tooltipHtml = renderToStaticMarkup(
    <DatasetTooltipText data={props.data} time={props.time} dataset={props.dataset} />
  );

  return (
    <div
      className={`button-clickable${props.selected ? " selected-label" : ""}${props.compactMode ? " compact-button-wrapper" : " button-wrapper"}`}
      id={props.id ?? ""}
      onClick={onButtonClick}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
      style={{ fontWeight: 700, ...props.style }}
      data-tooltip-id="dween-global-tooltip"
      data-tooltip-html={tooltipHtml}
    >
      <i>
        {props.dataset.stringValueMapper ? (
          <MappedValue mapValue={props.data?.value} mapperId={props.dataset.stringValueMapper} />
        ) : (
          <>
            {formatDatasetValue(props.dataset, props.data)}
            {props.dataset?.unit && ` ${props.dataset.unit}`}
          </>
        )}
      </i>
    </div>
  );
};

export default DatasetButtonComponent;
